import { useCallback, useContext } from 'react';

import { FEATURE } from '~/common/enums/feature.enum';
import { useTreatment } from '~/domains/common/hooks/useTreatment';
import { reportEvent } from '~/domains/analytics';
import { PromotionsModalsContext } from '~/domains/promotions/Modals/PromotionsModalsContext';
import type { FullContest } from '~/domains/contest/domains/services/interfaces/full-contest.interface';

import { resolvePromoCardVisibility } from './resolvePromoCardVisibility';

/**
 * Owns the Contest Detail promo callout cards: computes each card's visibility from the
 * `ENABLE_CONTEST_DETAIL_MARKETPLACE_PROMO` flag and returns click handlers that fire the
 * "modal viewed" analytics event and open the corresponding modal via the promotions modal stack.
 */
export function useContestDetailPromoCards(contestData: FullContest) {
  const { isReady, treatment } = useTreatment(FEATURE.ENABLE_CONTEST_DETAIL_MARKETPLACE_PROMO);
  const { openModalUnique } = useContext(PromotionsModalsContext);

  const { showSplitEntry, showMarketplace } = resolvePromoCardVisibility(
    isReady,
    treatment,
    contestData
  );

  const contestId = contestData.id;

  const openSplitEntryModal = useCallback(() => {
    reportEvent('Contest > Split Entry Promo Modal Viewed', { contest_id: contestId });
    openModalUnique({ type: 'ContestSplitEntryPromoModal' });
  }, [contestId, openModalUnique]);

  const openMarketplaceModal = useCallback(() => {
    reportEvent('Contest > Marketplace Promo Modal Viewed', { contest_id: contestId });
    openModalUnique({ type: 'ContestMarketplacePromoModal' });
  }, [contestId, openModalUnique]);

  return {
    showSplitEntry,
    showMarketplace,
    openSplitEntryModal,
    openMarketplaceModal,
  };
}
